import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/ErrorBanner";
import { Loader } from "../components/Loader";
import { tg } from "../lib/telegram";
import { getPremium, type PremiumStatus } from "../api/premium";
import { getRewards, redeemReward, type RewardItem } from "../api/rewards";

const FEATURES: Array<{ icon: string; titleKey: string; descKey: string }> = [
  { icon: "🎯", titleKey: "premium.features.createTitle", descKey: "premium.features.createDesc" },
  { icon: "🔍", titleKey: "premium.features.filtersTitle", descKey: "premium.features.filtersDesc" },
  { icon: "✅", titleKey: "premium.features.verifiedTitle", descKey: "premium.features.verifiedDesc" },
];

export function Premium() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PremiumStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const [monthReward, setMonthReward] = useState<RewardItem | null>(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    getPremium()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    getRewards()
      .then(({ balance, items }) => {
        setBalance(balance);
        const month = items.find(
          (r) => r.kind === "premium_days" && (r.metadata as { days?: number }).days === 30,
        );
        setMonthReward(month ?? null);
      })
      .catch(() => {});
  }, []);

  async function redeemMonth() {
    if (!monthReward || redeeming) return;
    setRedeeming(true);
    try {
      const res = await redeemReward(monthReward.id);
      setBalance(res.new_balance);
      tg()?.HapticFeedback?.impactOccurred("medium");
      getPremium().then(setData).catch(() => {});
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      tg()?.showAlert?.(t("premium.redeemFailed", { msg }));
    } finally {
      setRedeeming(false);
    }
  }

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.show();
    const onBack = () => navigate(-1);
    app?.BackButton.onClick(onBack);
    return () => {
      app?.BackButton.offClick(onBack);
      app?.BackButton.hide();
    };
  }, [navigate]);

  if (error) return <ErrorBanner message={t("premium.errorLoad", { error })} />;
  if (!data) return <Loader text={t("common.loading")} full />;

  return (
    <div className="min-h-screen bg-surface-soft pb-12">
      <div
        className="relative px-5 pt-10 pb-8 text-center"
        style={{
          background:
            "radial-gradient(ellipse 100% 70% at 50% 0%, var(--color-brand-purple-soft) 0%, transparent 70%), var(--color-surface)",
        }}
      >
        <div className="inline-flex items-center gap-2 bg-brand-purple text-white text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
          <StarIcon />
          {t("premium.label")}
        </div>
        <h1 className="text-[28px] font-extrabold text-ink-900 mt-3 leading-tight">
          {t("premium.title")}
        </h1>
        {data.active ? (
          <div className="mt-3 inline-block bg-brand-green-soft text-brand-green text-[14px] font-semibold px-4 py-2 rounded-full">
            {t("premium.activeUntil", { date: formatDate(data.premium_until!, i18n.language) })}
          </div>
        ) : (
          <p className="text-[14px] text-ink-500 mt-2 max-w-[320px] mx-auto leading-relaxed">
            {t("premium.subtitle")}
          </p>
        )}
      </div>

      {monthReward && (
        <div className="px-5 mt-6">
          <div className="bg-brand-yellow-soft rounded-[var(--radius-lg)] p-4 border border-brand-yellow/30">
            <div className="flex items-baseline justify-between">
              <div className="text-[15px] font-bold text-ink-900">
                {t("premium.payWithPointsTitle")}
              </div>
              <div className="text-[13px] text-ink-500">
                {t("premium.pointsBalance", { count: balance })}
              </div>
            </div>
            <Button
              stretched
              size="md"
              className="mt-3"
              onClick={() => void redeemMonth()}
              disabled={redeeming || balance < monthReward.cost_points}
            >
              {redeeming
                ? t("premium.buying")
                : balance < monthReward.cost_points
                  ? t("premium.notEnoughPoints", { cost: monthReward.cost_points })
                  : t("premium.buyWithPoints", { cost: monthReward.cost_points })}
            </Button>
          </div>
        </div>
      )}

      <div className="px-5 mt-6">
        <SectionLabel>{t("premium.whatsIncluded")}</SectionLabel>
        <div className="bg-surface rounded-[var(--radius-md)] border border-ink-100 divide-y divide-ink-100 overflow-hidden">
          {FEATURES.map((f, i) => (
            <FeatureRow key={i} icon={f.icon} title={t(f.titleKey)} desc={t(f.descKey)} />
          ))}
        </div>
      </div>

    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-400 mb-3">
      {children}
    </div>
  );
}

function FeatureRow({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="p-4 flex gap-3 items-start">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-purple-soft flex items-center justify-center text-[18px]">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-[15px] font-semibold text-ink-900 leading-tight">{title}</div>
        <div className="text-[13px] text-ink-500 mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function formatDate(iso: string, lang: string): string {
  return new Date(iso).toLocaleDateString(lang, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
